import type { StreamSource } from "@/types/match";
import { ENDPOINTS, USE_MOCK } from "./endpoints";

export interface ResolvedStream {
  streams: StreamSource[];
  watchUrl: string | null;
}

/**
 * API-Football (like most stats providers) has no concept of video — it
 * only knows scores and schedules. This is the one seam where your actual
 * video source plugs in: a signed URL from your own CDN/origin, a mapping
 * to a licensed partner's feed, or — for matches you don't hold streaming
 * rights to, which is the realistic case for most real fixtures — a
 * "watch on [broadcaster]" deep link instead of an embeddable stream.
 *
 * The proxy's GET /streams/:fixtureId route (server/index.js) currently
 * returns `{ streams: [], watchUrl: null }` by default — wire your real
 * lookup in there. This function is intentionally called separately from
 * the match-data fetch (not bundled into every fixture in a list) so
 * resolving it — which might hit your CDN, a database, or a partner API —
 * only happens for the one match someone actually opened, not for every
 * card in a list.
 */
export async function resolveStreamSources(matchId: string): Promise<ResolvedStream> {
  if (USE_MOCK) return { streams: [], watchUrl: null }; // mock matches already carry their own `streams` field directly

  try {
    const res = await fetch(ENDPOINTS.streamsForMatch(matchId));
    if (!res.ok) return { streams: [], watchUrl: null };
    const data = (await res.json()) as Partial<ResolvedStream>;
    return { streams: data.streams ?? [], watchUrl: data.watchUrl ?? null };
  } catch {
    // A broken stream lookup shouldn't break the whole match page —
    // VideoPlayer already renders a clean "not live yet" state for [].
    return { streams: [], watchUrl: null };
  }
}
